/**
 * Product Gallery — arrows + auto-rotate (PDP gallery images).
 *
 * Drives whichever carousel is actually live at the current viewport:
 *
 *   desktop (>=1000px)  the hero carousel in the highlighted full-width slot
 *                       (snippets/product-gallery.liquid). The transparent
 *                       header pins the gallery to the 2x grid up here, and a
 *                       grid has nothing to advance through, so the hero is
 *                       what carries the arrows and the rotation while the
 *                       grid below it stays fixed.
 *   mobile  (<1000px)   the main .product-gallery__carousel, which is a real
 *                       snapping carousel at that width and needs no hero.
 *
 * Rotation stops permanently for the rest of the page view the moment a
 * shopper navigates manually. It never starts under prefers-reduced-motion
 * (arrows still work), and pauses while the tab is hidden without ever
 * un-stopping once stopped.
 *
 * Three bugs kept the original from ever rotating, all fixed here:
 *
 * 1. TIMING. It was a bare IIFE calling querySelectorAll at parse time, but
 *    sections/main-product.liquid emits its <script> tag *above* the
 *    {% render 'product-gallery' %} call — <product-gallery> did not exist yet,
 *    so it initialized zero galleries on every load. Now it waits for
 *    DOMContentLoaded and for the custom element to be upgraded.
 *
 * 2. CAROUSEL API, NOT RAW SCROLL. It computed offsetLeft and called
 *    carousel.scrollTo, which fights <scroll-carousel>: scrolling underneath it
 *    leaves its selectedIndex — and so the thumbnails and dots — pointing at
 *    the wrong image. Now it calls next()/previous(), the same public API every
 *    other control uses.
 *
 * 3. STOP-ON-INTERACT VIA DELEGATION. stop() was bound directly to arrow
 *    buttons, but assets/gallery-navigation.js creates those in its own
 *    DOMContentLoaded handler, registered after this file's — the buttons did
 *    not exist yet when the listeners were attached. A delegated capture-phase
 *    listener on the gallery is immune to that ordering.
 */
(function () {
  var MANUAL_CONTROL_SELECTOR = [
    '[data-gallery-hero-prev]',
    '[data-gallery-hero-next]',
    '[data-gallery-autorotate-prev]',
    '[data-gallery-autorotate-next]',
    '.gallery-nav-arrow',
    '.product-gallery__thumbnail',
    '.page-dots button',
    'carousel-prev-button button',
    'carousel-next-button button',
  ].join(', ');

  function isUsable(carousel) {
    return !!carousel && typeof carousel.select === 'function' && carousel.cells.length > 1;
  }

  /**
   * Advance with wrap-around.
   *
   * NOT carousel.next()/previous(): <scroll-carousel> overrides those to CLAMP
   * (theme.js ~1065 — Math.min(i + 1, cells.length - 1) / Math.max(i - 1, 0)),
   * unlike the base Carousel class it inherits from, which wraps with modulo.
   * Calling next() therefore walks to the last image and then stops dead, so
   * auto-rotate froze on the final slide and the arrows went inert at both
   * ends. select() takes an absolute index, so the wrap is done here.
   */
  function step(carousel, direction) {
    var count = carousel.cells.length;
    if (!count) return;
    carousel.select((carousel.selectedIndex + direction + count) % count);
  }

  // offsetParent goes null for a display:none ancestor, which is exactly how
  // the hero is hidden below 1000px.
  function isVisible(element) {
    return !!element && element.offsetParent !== null;
  }

  // The theme sets scroll-snap-type:none for every grid layout and leaves it
  // "x mandatory" for carousels, so this one read answers "is this element
  // behaving as a carousel right now?" without hardcoding a breakpoint or
  // reading the layout setting.
  function isSnapping(carousel) {
    var snap = window.getComputedStyle(carousel).scrollSnapType;
    return !!snap && snap !== 'none';
  }

  function init(gallery) {
    if (gallery.dataset.autorotateInit) return;

    var heroRegion = gallery.querySelector('[data-gallery-hero]');
    var heroCarousel = heroRegion && heroRegion.querySelector('.product-gallery__hero-carousel');
    var mainCarousel = gallery.querySelector('.product-gallery__carousel');

    if (!isUsable(heroCarousel)) heroCarousel = null;
    if (!isUsable(mainCarousel)) mainCarousel = null;
    if (!heroCarousel && !mainCarousel) return;

    gallery.dataset.autorotateInit = 'true';

    var interval = parseInt(gallery.getAttribute('autorotate-interval'), 10) || 4000;
    var timer = null;

    // `stopped` is the shopper's one-way opt-out. Layout state is separate and
    // flips freely as the viewport is resized.
    var stopped = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function activeCarousel() {
      if (heroCarousel && isVisible(heroRegion)) return heroCarousel;
      if (mainCarousel && isSnapping(mainCarousel)) return mainCarousel;
      return null;
    }

    function clear() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function start() {
      if (stopped || timer || document.hidden || !activeCarousel()) return;
      timer = setInterval(function () {
        var carousel = activeCarousel();
        if (carousel) step(carousel, 1);
        else clear();
      }, interval);
    }

    function stop() {
      stopped = true;
      clear();
    }

    function bindArrow(selector, carousel, direction) {
      if (!carousel) return;
      gallery.querySelectorAll(selector).forEach(function (button) {
        button.addEventListener('click', function () {
          step(carousel, direction);
        });
      });
    }

    bindArrow('[data-gallery-hero-prev]', heroCarousel, -1);
    bindArrow('[data-gallery-hero-next]', heroCarousel, 1);
    bindArrow('[data-gallery-autorotate-prev]', mainCarousel, -1);
    bindArrow('[data-gallery-autorotate-next]', mainCarousel, 1);

    // Capture phase, so a control that stops propagation still ends rotation.
    gallery.addEventListener(
      'click',
      function (event) {
        if (event.target.closest(MANUAL_CONTROL_SELECTOR)) stop();
      },
      true
    );

    [heroCarousel, mainCarousel].forEach(function (carousel) {
      if (!carousel) return;
      carousel.addEventListener('pointerdown', stop, { once: true });
      carousel.addEventListener('touchstart', stop, { once: true, passive: true });
    });

    // Published for the stylesheet: the main carousel's own arrows are dead
    // controls whenever it is laid out as a grid, so they are hidden then. The
    // hero's arrows are not covered by this — the hero is only ever in the DOM
    // at widths where it is a carousel.
    function syncLayout() {
      var mainLive = !!mainCarousel && isSnapping(mainCarousel);
      gallery.dataset.mainCarouselActive = mainLive ? 'true' : 'false';

      if (activeCarousel()) start();
      else clear();
    }

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(syncLayout, 150);
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) clear();
      else start();
    });

    syncLayout();
  }

  function initAll() {
    document.querySelectorAll('product-gallery[autorotate-interval]').forEach(init);
  }

  function ready() {
    // theme.js defines <scroll-carousel>; before it upgrades, next() is not a
    // function yet and init() would bail.
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('scroll-carousel').then(initAll);
    } else {
      initAll();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready);
  } else {
    ready();
  }
})();
